/**
 * AdminMfaSetupPage — /admin/mfa/setup
 *
 * Reached when loginAdmin returns { mfaSetupRequired: true, setupToken }.
 * Three-step wizard:
 *   Step 1 — Scan QR code (or enter secret manually)
 *   Step 2 — Save backup codes (mandatory "I've saved these" gate)
 *   Step 3 — Enter first TOTP code to confirm enrollment
 *
 * On enrollment confirmed: sets auth state and redirects to dashboard.
 * Design: matches AdminLoginPage — clinical-charcoal bg, amber accent, white card.
 * Accessibility: step indicator, focus management, aria-live for announcements.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function ShieldIcon() {
  return (
    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// QR Code renderer — rendered client-side via the `qrcode` library.
// Self-contained: no third-party network call, works offline.
// ---------------------------------------------------------------------------

function QrCode({ uri, size = 200 }: { uri: string; size?: number }) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    setDataUrl(null);
    QRCode.toDataURL(uri, { width: size, margin: 1, errorCorrectionLevel: 'M' })
      .then((url) => { if (!cancelled) setDataUrl(url); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [uri, size]);

  if (failed) {
    return (
      <div
        style={{ width: size, height: size }}
        className="rounded-lg border border-gray-100 mx-auto bg-gray-50 flex items-center justify-center text-xs text-gray-500 px-2 text-center"
      >
        QR unavailable — use the manual code below
      </div>
    );
  }
  if (!dataUrl) {
    return (
      <div
        style={{ width: size, height: size }}
        className="rounded-lg border border-gray-100 mx-auto bg-gray-50 animate-pulse"
        aria-label="Generating QR code"
      />
    );
  }
  return (
    <img
      src={dataUrl}
      alt="Scan this QR code with your authenticator app"
      width={size}
      height={size}
      className="rounded-lg border border-gray-100 mx-auto"
    />
  );
}

// ---------------------------------------------------------------------------
// 6-digit OTP input
// ---------------------------------------------------------------------------

interface OtpInputProps {
  onComplete: (code: string) => void;
  disabled?: boolean;
  error?: boolean;
}

function OtpInput({ onComplete, disabled, error }: OtpInputProps) {
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''));
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, val: string) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...digits];
    next[index] = val;
    setDigits(next);
    if (val && index < 5) inputs.current[index + 1]?.focus();
    if (next.every(Boolean)) onComplete(next.join(''));
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) inputs.current[index - 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(''));
      onComplete(pasted);
      inputs.current[5]?.focus();
    }
  };

  return (
    <div className="flex gap-2 justify-center" role="group" aria-label="6-digit authentication code">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { inputs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          disabled={disabled}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          aria-label={`Digit ${i + 1} of 6`}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          className={[
            'w-10 h-12 text-center text-xl font-mono font-semibold rounded-lg border-2 outline-none transition-all',
            'focus:border-clinical-amber focus:ring-2 focus:ring-clinical-amber/20',
            error ? 'border-red-400 bg-red-50' : d ? 'border-clinical-amber bg-amber-50' : 'border-gray-200 bg-white',
            'text-clinical-charcoal',
          ].join(' ')}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step indicator
// ---------------------------------------------------------------------------

const STEP_LABELS = ['Scan QR', 'Save Codes', 'Verify'];

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-2 mb-6" role="navigation" aria-label="Setup progress">
      {STEP_LABELS.map((label, i) => (
        <div key={i} className="flex items-center gap-1">
          <div
            className={[
              'flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold',
              i < current
                ? 'bg-clinical-amber text-white'
                : i === current
                ? 'border-2 border-clinical-amber text-clinical-amber'
                : 'border border-gray-200 text-gray-300',
            ].join(' ')}
            aria-current={i === current ? 'step' : undefined}
          >
            {i < current ? <CheckIcon /> : i + 1}
          </div>
          <span className={`text-xs font-body ${i === current ? 'text-clinical-charcoal font-medium' : 'text-gray-400'}`}>
            {label}
          </span>
          {i < STEP_LABELS.length - 1 && <span className="text-gray-200 mx-1">·</span>}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page types
// ---------------------------------------------------------------------------

interface EnrollResponse {
  secret: string;
  qrUri: string;
  backupCodes: string[];
}

interface VerifyEnrollResponse {
  accessToken: string;
  admin: { id: string; email: string; role: string };
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AdminMfaSetupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { acceptMfaToken } = useAuth();

  const state = location.state as { setupToken?: string } | null;
  const setupToken = state?.setupToken ?? '';

  const [step, setStep] = useState(0);
  const [enrollData, setEnrollData] = useState<EnrollResponse | null>(null);
  const [showManual, setShowManual] = useState(false);
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [liveAnnouncement, setLiveAnnouncement] = useState('');

  const announce = (msg: string) => setLiveAnnouncement(msg);

  // Redirect if no setupToken
  useEffect(() => {
    if (!setupToken) {
      void navigate('/admin/login', { replace: true });
    }
  }, [setupToken, navigate]);

  // Initiate enrollment on mount
  useEffect(() => {
    if (!setupToken) return;
    setIsLoading(true);
    api.post<EnrollResponse>('/admin/auth/mfa/enroll', { setupToken })
      .then((data) => { setEnrollData(data); })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Failed to initiate enrollment.';
        setError(msg);
      })
      .finally(() => setIsLoading(false));
  }, [setupToken]);

  const downloadBackupCodes = useCallback(() => {
    if (!enrollData) return;
    const text = [
      'Clinical Interpreter — Admin MFA Backup Codes',
      'Keep these in a safe place. Each code can only be used once.',
      '',
      ...enrollData.backupCodes.map((c, i) => `${i + 1}. ${c}`),
    ].join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'admin-mfa-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }, [enrollData]);

  const submitVerify = useCallback(async (code: string) => {
    setIsLoading(true);
    setError('');
    try {
      const res = await api.post<VerifyEnrollResponse>('/admin/auth/mfa/verify-enroll', {
        setupToken,
        code,
      });
      announce('Enrollment confirmed. Signing you in…');
      acceptMfaToken(res.accessToken, {
        id: res.admin.id,
        email: res.admin.email,
        role: 'admin',
      });
      void navigate('/admin/dashboard', { replace: true });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Invalid code. Please try again.';
      setError(msg);
      announce(`Error: ${msg}`);
    } finally {
      setIsLoading(false);
    }
  }, [setupToken, acceptMfaToken, navigate]);

  if (!enrollData && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-clinical-charcoal">
        <div className="w-8 h-8 border-2 border-clinical-amber border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-clinical-charcoal px-4"
      style={{ backgroundImage: 'radial-gradient(ellipse at 50% 0%, rgba(212,165,116,0.08) 0%, transparent 70%)' }}
    >
      <div aria-live="polite" aria-atomic="true" className="sr-only">{liveAnnouncement}</div>

      <div className="w-full max-w-sm">
        {/* Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 text-clinical-amber mb-4">
            <ShieldIcon />
          </div>
          <h1 className="font-heading text-2xl font-semibold text-white">Clinical Interpreter</h1>
          <p className="text-sm font-body text-white/50 mt-1 uppercase tracking-widest">Admin Portal</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-clinical-lg p-8">
          <StepIndicator current={step} />

          {error && !enrollData && (
            <div className="rounded-clinical border border-red-200 bg-red-50 px-4 py-3 mb-4">
              <p className="text-sm font-body text-red-700">{error}</p>
            </div>
          )}

          {/* Step 0 — Scan QR */}
          {step === 0 && enrollData && (
            <div className="space-y-5">
              <div>
                <h2 className="font-heading text-lg font-semibold text-clinical-charcoal mb-1">
                  Scan with your authenticator
                </h2>
                <p className="text-sm font-body text-clinical-text-muted">
                  Open Google Authenticator, Authy, or 1Password and scan this QR code.
                </p>
              </div>

              <div style={{ animation: 'fadeUp 600ms ease both' }}>
                <QrCode uri={enrollData.qrUri} size={200} />
              </div>

              <button
                type="button"
                onClick={() => setShowManual((v) => !v)}
                className="text-xs font-body text-clinical-amber hover:underline w-full text-center"
              >
                {showManual ? 'Hide manual entry' : "Can't scan? Enter manually"}
              </button>

              {showManual && (
                <div className="rounded-lg bg-gray-50 p-3 border border-gray-100">
                  <p className="text-xs font-body text-gray-500 mb-1">Manual entry secret:</p>
                  <p className="font-mono text-sm text-clinical-charcoal break-all select-all">{enrollData.secret}</p>
                </div>
              )}

              <button
                type="button"
                onClick={() => { setStep(1); announce('Step 2 of 3: Save your backup codes'); }}
                className="btn-primary w-full"
              >
                I've scanned it — Next
              </button>
            </div>
          )}

          {/* Step 1 — Backup Codes */}
          {step === 1 && enrollData && (
            <div className="space-y-5">
              <div>
                <h2 className="font-heading text-lg font-semibold text-clinical-charcoal mb-1">
                  Save your backup codes
                </h2>
                <p className="text-sm font-body text-clinical-text-muted">
                  If you lose your authenticator, these codes let you regain access. Each code can only be used once.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {enrollData.backupCodes.map((code, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 border border-gray-100"
                  >
                    <span className="font-mono text-xs text-clinical-charcoal tracking-widest">{code}</span>
                    <button
                      type="button"
                      title="Copy code"
                      onClick={() => void navigator.clipboard.writeText(code)}
                      className="text-clinical-amber hover:text-amber-700 ml-2 shrink-0"
                      aria-label={`Copy backup code ${i + 1}`}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={downloadBackupCodes}
                className="w-full text-sm font-body text-clinical-amber hover:underline flex items-center justify-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Download as .txt
              </button>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={savedConfirmed}
                  onChange={(e) => setSavedConfirmed(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-clinical-amber rounded"
                  aria-required="true"
                />
                <span className="text-sm font-body text-clinical-text-muted">
                  I've saved these backup codes in a safe place.
                </span>
              </label>

              <button
                type="button"
                disabled={!savedConfirmed}
                onClick={() => { setStep(2); announce('Step 3 of 3: Verify your authenticator code'); }}
                className="btn-primary w-full disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continue to verification
              </button>
            </div>
          )}

          {/* Step 2 — Verify */}
          {step === 2 && (
            <div className="space-y-5">
              <div className="text-center">
                <h2 className="font-heading text-lg font-semibold text-clinical-charcoal mb-1">
                  Confirm your authenticator
                </h2>
                <p className="text-sm font-body text-clinical-text-muted">
                  Enter the 6-digit code from your app to complete enrollment.
                </p>
              </div>

              <OtpInput
                onComplete={(c) => void submitVerify(c)}
                disabled={isLoading}
                error={Boolean(error)}
              />

              {error && (
                <div className="rounded-clinical border border-red-200 bg-red-50 px-4 py-3">
                  <p className="text-sm font-body text-red-700 text-center">{error}</p>
                </div>
              )}

              {isLoading && (
                <div className="flex justify-center">
                  <div className="w-5 h-5 border-2 border-clinical-amber border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
          )}
        </div>

        <p className="text-center text-xs font-body text-white/30 mt-6">
          Platform-level access only. Not for clinical use.
        </p>
      </div>

      {/* CSS-only step transition animations */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0);   }
        }
      `}</style>
    </div>
  );
}
